import {
    filterCannedMessagesByQueue, useCannedMessages,
} from 'hooks/cannedmessages/useSupportedLanguages'
import {ChatProps} from './types'
import ChatView from './ChatView'
import React from 'react'
import lodash from 'lodash'
import {mergeCannedMessages} from 'utils/cannedmessages'
import {useAppSelector} from 'hooks'
import {useQueues} from 'hooks/ninchat'

const Chat: React.FC<ChatProps> = (props: ChatProps): React.ReactElement => {
    const {
        chat,
        realm_id,
    } = props

    const {
        cannedMessages: organizationCannedMessages, cannedMessagesLanguages: organizationLanguages,
    } = useCannedMessages({
        organization: true,
        realmId: realm_id,
    })
    const {
        cannedMessages: userCannedMessages, cannedMessagesLanguages: userLanguages,
    } = useCannedMessages({
        organization: false,
        realmId: realm_id,
    })

    const supportedCannedMessagesLanguages = lodash.unionBy([...userLanguages, ...organizationLanguages], 'id')

    const isClosed = useAppSelector(({ninchat}) => {
        if (chat && chat.channel_id) {
            return Boolean(ninchat.user_channels[chat.channel_id]?.channel_attrs?.closed)
        }

        return false
    })

    const isSuspended = useAppSelector(({ninchat}) => {
        if (chat && chat.channel_id) {
            return Boolean(ninchat.user_channels[chat.channel_id]?.channel_attrs?.suspended)
        }

        return false
    })

    const queueId = useAppSelector(({ninchat}) => {
        if (chat && chat.channel_id) {
            return ninchat.user_channels[chat.channel_id]?.channel_attrs?.queue_id
        }

        return ''
    }) || ''

    const disableSharedCannedMessages = useAppSelector(({ninchat}) => ninchat.user_settings.canned_messages_disable_shared)
    const currentQueue = useQueues([queueId])[queueId] ?? {}
    const notes = currentQueue?.queue_settings?.notes || []
    const isQueueFilterEnabled = currentQueue?.queue_settings?.filter_shared_canned_messages ?? false
    const videoAutoJoin = currentQueue?.queue_attrs?.video_autojoin
    // when queue filter is enabled, filter organization canned messages by queue
    const filteredOrganizationCannedMessages = isQueueFilterEnabled
        ? filterCannedMessagesByQueue(organizationCannedMessages, queueId, currentQueue?.queue_settings)
        : organizationCannedMessages
    const sharedCannedMessages = mergeCannedMessages({
        organizationCannedMessages: filteredOrganizationCannedMessages,
        userCannedMessages,
    })

    return (
        <ChatView
            {...props}
            cannedMessages={disableSharedCannedMessages ? userCannedMessages : sharedCannedMessages}
            isClosed={isClosed}
            isSuspended={isSuspended}
            notes={notes}
            supportedCannedMessagesLanguages={disableSharedCannedMessages ? userLanguages : supportedCannedMessagesLanguages}
            videoAutoJoin={videoAutoJoin}
        />
    )
}

export default Chat
